import Vue from "vue";
import { bus } from "@/utils/eventBus";
import {
  Cadastrar,
  VerificarCadastro,
  Autenticar,
  GerarQRCode,
  VerificarQRCode,
  RegistrarErro
} from "../api/biometria";

const getNativeBridge = () => Vue.prototype.$native;

const getInitialState = () => {
  const initialState = {
    hasBiometric: false,
    shoudUseBiometric: false,
    isUserAuthenticated: false,
    listenersRegistred: false,
    nativeShareEnabled: false,
    storage: "",
    camera: "",
    microfone: "",
    location: ""
  };
  const rawShouldUseBiometric = localStorage.getItem("biometrics/shouldUseBiometric");
  if (rawShouldUseBiometric === "yes") {
    initialState.shoudUseBiometric = true;
  }
  return initialState;
};

const OnUserAuth = dispatch => message => {
  const { auth } = JSON.parse(message);
  if (auth) {
    dispatch("setAuthenticated");
  }
};

const OnEnableBiometric = dispatch => message => {
  const { status } = JSON.parse(message);
  dispatch("setBiometricEnabled", status);
};

const OnIsAvailable = dispatch => message => {
  const { available } = JSON.parse(message);
  dispatch("setBiometric", available);
};

const onNativeShareIsAvailable = dispatch => message => {
  const { available } = JSON.parse(message);
  dispatch("setNativeShare", available);
};

const onResourceStatusChange = dispatch => message => {
  const { storageState, cameraState, microfoneState, locationState } = JSON.parse(message);
  if (storageState) {
    dispatch("setStorageState", storageState);
  }
  if (cameraState) {
    dispatch("setCameraState", cameraState);
  }
  if (microfoneState) {
    dispatch("setMicrofoneState", microfoneState);
  }
  if (locationState) {
    dispatch("setLocationState", locationState);
  }
};

// vuex module
export default {
  namespaced: true,
  state: getInitialState,
  getters: {
    onNative: () => {
      if (process.env.VUE_APP_FORCE_NATIVE === "true") return true;
      return Boolean(getNativeBridge());
    }
  },
  actions: {
    registerListeners(context) {
      if (!context.state.listenersRegistred && getNativeBridge()) {
        bus.$on("biometrics:isAvailable", OnIsAvailable(context.dispatch));
        bus.$on("biometrics:enable", OnEnableBiometric(context.dispatch));
        bus.$on("biometrics:auth", OnUserAuth(context.dispatch));
        bus.$on("native_share:isAvailable", onNativeShareIsAvailable(context.dispatch));
        bus.$on("resource_status", onResourceStatusChange(context.dispatch));
        context.commit("SET_LISTENERS_REGISTRED", true);
      }
    },
    cleanStorage() {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      return nativeBridge.postMessage(JSON.stringify({ type: "clean_storage" }));
    },
    authenticateUser(context) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      context.commit("SET_IS_USER_AUTHENTICATED", false);
      nativeBridge.postMessage(JSON.stringify({ type: "biometrics:auth" }));
    },
    enableBiometric() {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      nativeBridge.postMessage(JSON.stringify({ type: "biometrics:enable" }));
    },
    checkForBiometric(context) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      if (!context.state.hasBiometric) {
        nativeBridge.postMessage(JSON.stringify({ type: "biometrics:isAvailable" }));
      }
    },
    checkForNativeShare(_) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      nativeBridge.postMessage(JSON.stringify({ type: "native_share:isAvailable" }));
    },
    setAuthenticated(context) {
      if (!getNativeBridge()) return;
      context.commit("SET_IS_USER_AUTHENTICATED", true);
    },
    setBiometric(context, hasBiometric) {
      if (!getNativeBridge()) return;
      context.commit("SET_HAS_BIOMETRIC", hasBiometric);
    },
    setBiometricEnabled(context, status) {
      if (!getNativeBridge()) return;
      context.commit("SET_SHOULD_USE_BIOMETRIC", status);
      context.commit("SET_IS_USER_AUTHENTICATED", status);
    },
    disableBiometric(context) {
      if (!getNativeBridge()) return;
      context.commit("SET_SHOULD_USE_BIOMETRIC", false);
      context.commit("SET_IS_USER_AUTHENTICATED", false);
    },
    shareImage(context, { title, data }) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return false;
      if (context.state.nativeShareEnabled) {
        nativeBridge.postMessage(JSON.stringify({ type: "share_image_binary", payload: { title, data } }));
      }
      return context.state.nativeShareEnabledue;
    },
    setNativeShare(context, status) {
      if (!getNativeBridge()) return;
      context.commit("SET_HAS_NATIVE_SHARE", status);
    },
    setUserAuthenticationState(_, authenticated) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return;
      return nativeBridge.postMessage(
        JSON.stringify({
          type: authenticated ? "authenticated:true" : "authenticated:false"
        })
      );
    },
    requestPermissionForResource(_, resource) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return false;
      nativeBridge.postMessage(JSON.stringify({ type: "request_permission", payload: { resource } }));
      return true;
    },
    openAppSettings() {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return false;
      nativeBridge.postMessage(JSON.stringify({ type: "open_app_settings", payload: {} }));
      return true;
    },
    checkPermissionForResource(_, resource) {
      const nativeBridge = getNativeBridge();
      if (!nativeBridge) return false;
      nativeBridge.postMessage(JSON.stringify({ type: "check_permission", payload: { resource } }));
      return true;
    },
    setStorageState(context, state) {
      context.commit("SET_STORAGE_STATE", state);
    },
    setMicrofoneState(context, state) {
      context.commit("SET_MICROFONE_STATE", state);
    },
    setCameraState(context, state) {
      context.commit("SET_CAMERA_STATE", state);
    },
    setLocationState(context, state) {
      context.commit("SET_LOCATION_STATE", state);
    },
    async authFace(context, { service, selfie }) {
      if (service && !service.startsWith("/")) {
        try {
          service = context.rootGetters[service];
        } catch (error) {
          console.error(error);
          globalThis.window.$log.message(`using service ${service}`);
        }
      }
      const response = await Autenticar({
        vinculo: context.rootState.saude.vinculo,
        servico: service,
        selfie
      });
      if (response instanceof Error) throw new Error(response.message ?? "Não foi possível realizar a autenticação");
      return response.Data;
    },
    async saveFace(context, { selfie }) {
      const response = await Cadastrar({
        vinculo: context.rootState.saude.vinculo,
        selfie
      });
      if (response instanceof Error)
        throw new Error(response.message ?? "Não foi possível realizar o cadastro da biometria");
      return response.Data;
    },
    async fetchFaceStatus(context, _) {
      const response = await VerificarCadastro({
        vinculo: context.rootState.saude.vinculo
      });
      if (response instanceof Error)
        throw new Error(response.message ?? "Não foi possível verificar a situação do cadastro");
      return response.Data;
    },
    async registerError(context, { cod, dsc }) {
      const response = await RegistrarErro({
        vinculo: context.rootState.saude.vinculo,
        codigo: cod,
        descricao: dsc
      });
      return response?.Data ?? {};
    },
    async createAuthQRCode(context, { service, phone }) {
      const response = await GerarQRCode({
        vinculo: context.rootState.saude.vinculo,
        servico: service,
        telefone: phone
      });
      if (response instanceof Error) throw new Error(response.message ?? "Não foi possível gerar o QRCode");
      return response.Data;
    },
    async fetchQRCodeStatus(context, { service, token, id }) {
      const response = await VerificarQRCode({
        vinculo: context.rootState.saude.vinculo,
        servico: service,
        token,
        id
      });
      if (response instanceof Error)
        throw new Error(response.message ?? "Não foi possível verificar a situação do QRCode");
      return response.Data;
    }
  },
  mutations: {
    LOGOUT_USER: state => state,
    RESET_CONTEXT: state => state,
    SET_HAS_BIOMETRIC: (state, value) => {
      Vue.set(state, "hasBiometric", value);
    },
    SET_SHOULD_USE_BIOMETRIC: (state, value) => {
      localStorage.setItem("biometrics/shouldUseBiometric", value ? "yes" : "no");
      Vue.set(state, "shoudUseBiometric", value);
    },
    SET_IS_USER_AUTHENTICATED: (state, value) => {
      localStorage.setItem("biometrics/isUserAuthenticated", value ? "yes" : "no");
      Vue.set(state, "isUserAuthenticated", value);
    },
    SET_LISTENERS_REGISTRED: (state, value) => {
      Vue.set(state, "listenersRegistred", value);
    },
    SET_HAS_NATIVE_SHARE: (state, value) => {
      Vue.set(state, "nativeShareEnabled", value);
    },
    SET_STORAGE_STATE: (state, value) => {
      Vue.set(state, "storage", value);
    },
    SET_MICROFONE_STATE: (state, value) => {
      Vue.set(state, "microfone", value);
    },
    SET_CAMERA_STATE: (state, value) => {
      Vue.set(state, "camera", value);
    },
    SET_LOCATION_STATE: (state, value) => {
      Vue.set(state, "location", value);
    }
  }
};
